import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Sparkles, Zap, Shield, Clock, DollarSign, Check, Loader2, Crown, Gavel } from "lucide-react";
import { toast } from "sonner";

const TIERS = [
  { key: "pass", name: "Driver Pass", price: 19, fee: "5%", accent: "cyan",
    perks: [{ icon: DollarSign, t: "5% service fee", b: "Half the standard 10% on every booking." },
            { icon: Zap, t: "Priority access", b: "Earliest access to gig-ready inventory in surge cities." },
            { icon: Clock, t: "Instant approvals", b: "Bookings auto-confirm when docs are on file." }] },
  { key: "pro", name: "Driver Pass Pro", price: 49, fee: "0%", accent: "amber", featured: true,
    perks: [{ icon: DollarSign, t: "0% service fee", b: "Keep every dollar — no platform fee on bookings." },
            { icon: Crown, t: "Priority support badge", b: "Shown to hosts and support; your tickets go to the front." },
            { icon: Gavel, t: "Priority dispute handling", b: "Your disputes are reviewed first in the operator queue." },
            { icon: Shield, t: "Everything in Driver Pass", b: "Priority access + instant approvals included." }] },
];

export default function Membership() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirming, setConfirming] = useState(false);

  const load = () => api.get("/membership/status").then((r) => setStatus(r.data)).catch(() => {});

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    const sid = params.get("session_id");
    if (sid) {
      setConfirming(true);
      api.get(`/membership/confirm/${sid}`).then(() => { toast.success("Membership activated!"); setConfirming(false); load(); refresh?.(); })
        .catch(() => { setConfirming(false); load(); });
    } else load();
    if (params.get("cancelled")) toast.info("Checkout cancelled — no charge made.");
    // eslint-disable-next-line
  }, [user]);

  const subscribe = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/membership/checkout", { origin_url: window.location.origin, plan });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); setBusy(""); }
  };

  const current = status?.plan;
  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-8 py-14" data-testid="membership-page">
      <div className="text-center mb-10 rise">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 mb-5">
          <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-xs font-mono uppercase tracking-widest text-emerald-300">Driver Pass</span>
        </div>
        <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tight">Pay less. Drive more.</h1>
        <p className="text-slate-300 mt-4 max-w-2xl mx-auto">Two tiers. Both pay for themselves on your first weekend. Cancel anytime.</p>
      </div>

      {confirming ? (
        <div className="text-center py-12"><Loader2 className="w-10 h-10 text-cyan-400 animate-spin mx-auto mb-4" /><p className="text-slate-300">Activating your membership…</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          {TIERS.map((t, i) => {
            const active = current === t.key;
            const isUpgrade = current === "pass" && t.key === "pro";
            const locked = current === "pro" && t.key === "pass";
            return (
              <div key={t.key} data-testid={`tier-${t.key}`} className={`relative rounded-3xl p-8 rise rise-${i + 1} border ${t.featured ? "bg-gradient-to-br from-amber-500/15 via-orange-500/5 to-slate-900 border-amber-500/40" : "glass border-slate-800"}`}>
                {t.featured && <span className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-amber-500 text-slate-950 text-[10px] font-bold font-mono uppercase tracking-widest">Most popular</span>}
                <div className="flex items-start justify-between gap-3">
                  <div><p className={`font-mono text-xs tracking-widest uppercase mb-1 ${t.featured ? "text-amber-300" : "text-cyan-400"}`}>{t.name}</p>
                    <div className="flex items-baseline gap-2"><span className="font-display text-5xl font-extrabold">${t.price}</span><span className="text-slate-400">/ month</span></div></div>
                  <Badge className={`${t.featured ? "bg-amber-500/20 text-amber-300 border-amber-500/30" : "bg-cyan-500/20 text-cyan-300 border-cyan-500/30"} border font-mono`}>FEE {t.fee}</Badge>
                </div>
                <div className="space-y-4 mt-7">
                  {t.perks.map((p, j) => (
                    <div key={j} className="flex gap-3" data-testid={`perk-${t.key}-${j}`}>
                      <div className="w-9 h-9 shrink-0 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center"><p.icon className={`w-4 h-4 ${t.featured ? "text-amber-400" : "text-cyan-400"}`} /></div>
                      <div><p className="font-display font-bold text-sm">{p.t}</p><p className="text-xs text-slate-400">{p.b}</p></div>
                    </div>
                  ))}
                </div>
                <div className="mt-8">
                  {active ? (
                    <div className="text-center p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30" data-testid={`active-${t.key}`}>
                      <p className="text-emerald-300 font-semibold flex items-center justify-center gap-2"><Check className="w-4 h-4" strokeWidth={3} />Your current plan</p>
                      {status?.started_at && <p className="text-xs text-slate-400 mt-1">Active since {new Date(status.started_at).toLocaleDateString()}</p>}
                    </div>
                  ) : (
                    <Button onClick={() => subscribe(t.key)} disabled={!!busy || locked} data-testid={`subscribe-${t.key}-btn`}
                      className={`w-full h-12 font-semibold ${t.featured ? "bg-amber-500 text-slate-950 hover:bg-amber-400 btn-glow" : "bg-white text-slate-950 hover:bg-slate-100"}`}>
                      {busy === t.key ? "Opening checkout…" : locked ? "Included in Pro" : isUpgrade ? "Upgrade to Pro" : `Start ${t.name}`}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}
